import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';
import { CourierShell, QuoteCard, Field, inputCls, C } from '../../components/courier/CourierUI';

export default function CourierQuote() {
  const nav = useNavigate();
  const [zones, setZones] = useState([]);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    originZone: 'lusaka', destZone: 'copperbelt', serviceLevel: 'express',
    weightKg: 1, lengthCm: 20, widthCm: 15, heightCm: 10, declaredValue: 0, insured: false, contents: '',
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/courier/zones').then(({ data }) => { setZones(data.zones); setServices(data.serviceLevels); });
  }, []);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const getQuote = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/courier/quote', {
        originZone: form.originZone, destZone: form.destZone, serviceLevel: form.serviceLevel,
        parcel: {
          weightKg: Number(form.weightKg), lengthCm: Number(form.lengthCm),
          widthCm: Number(form.widthCm), heightCm: Number(form.heightCm),
          declaredValue: Number(form.declaredValue), insured: form.insured, contents: form.contents,
        },
      });
      setResult(data);
    } catch (e) {
      toast({ title: 'Quote failed', description: e.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  return (
    <CourierShell>
      <h1 className="text-3xl font-black mb-1">Get a Quote</h1>
      <p className="text-black/60 mb-6">Instant zone-based pricing for your parcel.</p>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-2xl p-6 border border-black/5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="From">
              <select className={inputCls} value={form.originZone} onChange={(e) => set('originZone', e.target.value)} data-testid="quote-origin">
                {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
              </select>
            </Field>
            <Field label="To">
              <select className={inputCls} value={form.destZone} onChange={(e) => set('destZone', e.target.value)} data-testid="quote-dest">
                {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Service Level">
            <div className="grid grid-cols-2 gap-2">
              {services.map((s) => (
                <button key={s.id} type="button" onClick={() => set('serviceLevel', s.id)}
                  data-testid={`quote-service-${s.id}`}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${form.serviceLevel === s.id ? 'border-black' : 'border-black/10'}`}
                  style={form.serviceLevel === s.id ? { background: C.yellow } : {}}>
                  <p className="font-black text-sm">{s.name}</p>
                  <p className="text-[11px] text-black/60">{s.etaDomestic} domestic</p>
                </button>
              ))}
            </div>
          </Field>
          <Field label="Weight (kg)">
            <input type="number" min="0.1" step="0.1" className={inputCls} value={form.weightKg} onChange={(e) => set('weightKg', e.target.value)} data-testid="quote-weight" />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Length (cm)"><input type="number" className={inputCls} value={form.lengthCm} onChange={(e) => set('lengthCm', e.target.value)} data-testid="quote-length" /></Field>
            <Field label="Width (cm)"><input type="number" className={inputCls} value={form.widthCm} onChange={(e) => set('widthCm', e.target.value)} data-testid="quote-width" /></Field>
            <Field label="Height (cm)"><input type="number" className={inputCls} value={form.heightCm} onChange={(e) => set('heightCm', e.target.value)} data-testid="quote-height" /></Field>
          </div>
          <Field label="Contents (optional)">
            <input className={inputCls} value={form.contents} onChange={(e) => set('contents', e.target.value)} placeholder="Documents, electronics…" data-testid="quote-contents" />
          </Field>
          <div className="grid grid-cols-2 gap-3 items-end">
            <Field label="Declared Value (K)">
              <input type="number" className={inputCls} value={form.declaredValue} onChange={(e) => set('declaredValue', e.target.value)} data-testid="quote-declared" />
            </Field>
            <label className="flex items-center gap-2 text-sm font-bold pb-2.5">
              <input type="checkbox" checked={form.insured} onChange={(e) => set('insured', e.target.checked)} data-testid="quote-insured" className="h-4 w-4" />
              Insure parcel (1%)
            </label>
          </div>
          <button onClick={getQuote} disabled={loading} data-testid="quote-calculate-btn"
            className="w-full py-3.5 rounded-xl font-black text-black transition-transform hover:scale-[1.01]" style={{ background: C.yellow }}>
            {loading ? 'Calculating…' : 'Calculate Quote'}
          </button>
        </div>

        <div>
          {result ? (
            <QuoteCard quote={result.quote} quoteRef={result.quoteRef} qr={result.qr}
              onShip={() => nav('/courier/ship', { state: { quoteRef: result.quoteRef, quote: result.quote } })} />
          ) : (
            <div className="h-full min-h-[300px] rounded-2xl border-2 border-dashed border-black/15 flex items-center justify-center text-black/40 text-sm" data-testid="quote-empty">
              Your quote will appear here
            </div>
          )}
        </div>
      </div>
    </CourierShell>
  );
}
